import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "trial-banner"
}
const _hoisted_2 = { class: "trial-text" }
const _hoisted_3 = { class: "content-area" }

import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { navigateToUrl } from "single-spa";

/* ---------------------------------------------------------------------------
 * UI Components
 * ---------------------------------------------------------------------------
 */
import { TheHeader, DrawerHost } from "../components";
import {
  TheUnifiedNavigation,
  AlertModal,
  GenericModalState,
} from "@maropost-ui/liquidsky-ui";
import OnboardingMenu from "../components/onboarding/NavigationMenu.vue";

/* ---------------------------------------------------------------------------
 * Stores & Services
 * ---------------------------------------------------------------------------
 */
import { useUserStore } from "../stores";
import { useNavigation } from "../composable/sidemenu/unifiedNavigation";
import { alertModalService } from "@/lib/services/alertModalService";
import { onBoardingService } from "@/lib/services/onBoardingService";
import { shouldFetchOnboardingData } from "@/lib/utils/onboardingAccess";
import { useHubSpotChat } from "@/lib/plugins/hubspotChat";

import { useGlobalTrialState } from "../composable/freeTrail/useGlobalTrialState";

/* ============================================================================
 * STORES & ROUTER
 * ============================================================================
 */

// User / permissions store

export default /*@__PURE__*/_defineComponent({
  __name: 'AppLayout',
  setup(__props) {

/**
 * ============================================================================
 * Unified Shell – Navigation & Loader Controller
 * ============================================================================
 *
 * Behavior Summary
 * ----------------
 * - Displays unified navigation ONLY for supported product routes
 *   (Marketing, Service, Commerce).
 * - Navigation menu is loaded lazily and reacts to route changes.
 * - Global loader visibility is controlled via cross-MFE events.
 *
 * Communication Model
 * -------------------
 * - MFEs dispatch `MFE_LOADED` events to signal loading state.
 * - Shell listens and updates `loading` accordingly.
 * - MFEs can also request navigation rail changes via custom events.
 *
 * Design Notes
 * ------------
 * - Shell remains framework-agnostic.
 * - MFEs do not directly import shell dependencies.
 * - All UI side-effects are centralized here.
 * ============================================================================
 */

const userStore = useUserStore();

// Vue Router instance
const router = useRouter();
const route = useRoute();

// Global alert modal reactive state
const alertModalState = alertModalService.getState();

// Global loader state (controlled by MFEs)
const loading = ref(false);

// Show banner state
const showBanner = computed(() => userStore.getCTABanner);
const hubSpotChat = useHubSpotChat();

/**
 * Load HubSpot chat after the shell loader is idle (initial mount or MFE finished).
 */
const loadHubSpotWhenIdle = (): void => {
  if (!loading.value) {
    hubSpotChat.loadIfEnabled();
  }
};

onMounted(loadHubSpotWhenIdle);

watch(loading, loadHubSpotWhenIdle, { flush: "post" });

// Sales navigation visibility (permission-based)
const saleNavigation = computed(() => userStore.getSaleNavigation);

// Normalized alert modal props
const alertModalItems = computed<GenericModalState>(() => ({
  title: alertModalState.title,
  subtitle: alertModalState.subtitle,
  description: alertModalState.description,
}));

  /**
   * Get the trial data
   */
  const trialData:any = computed(() => userStore.getTrailPackageFeatures);

  /**
   * Use the trial countdown composable
   */

  const { trialDays } = useGlobalTrialState(
    trialData,
    userStore
  );

  /**
   * Hide the banner
   */
  const hideBanner = () => {
    userStore.setCTABanner(false);
  }

  /**
   * Navigate to the billing page
   */
  const navigateToBilling = () => {
    
    let accountId = userStore.getAccountId
     navigateToUrl(`/accounts/${accountId}/account-&-billing/subscriptions`)
  }

  /**
   * Check if the trial is expired
   */
   const isTrialExpired = computed(() => userStore.getIsTrailAccount && userStore.getIsTrialExpired);


   /**
    * 
    */
   const isNetoMhcAccountTraialExpired = computed(() => userStore.getIsTrailAccount && userStore.getIsTrialExpired && !userStore.getProductAccess('neto') && !userStore.getProductAccess('mhc'));

/**
 * Left navigation visibility.
 * Hidden on routes that opt out via meta (e.g. account setup) or when
 * a Neto/MHC trial account has expired.
 */
const showNavigation = computed(() => {
  const navigationEnabled = (route.meta.navigation as { show?: boolean } | undefined)?.show !== false
  return navigationEnabled && !isNetoMhcAccountTraialExpired.value
});

/* ============================================================================
 * NAVIGATION STATE
 * ============================================================================
 */

/**
 * Unified navigation configuration
 * - Generated dynamically based on user permissions
 * - Passed directly to <TheUnifiedNavigation />
 */
const { navigation } = useNavigation();

onMounted(() => {
  if (shouldFetchOnboardingData(userStore)) {
    onBoardingService.getOnboardingData().catch((err) => {
      console.error("Failed to fetch onboarding data:", err);
    });
  }
});

/* ============================================================================
 * CROSS-MFE EVENT HANDLERS
 * ============================================================================
 */

/**
 * Handle MFE loader lifecycle events
 *
 * Event: MFE_LOADED
 * Payload:
 *   - true  → show loader
 *   - false → hide loader
 *
 * Dispatched by:
 *   - Shell (before mounting/unmounting MFEs)
 *   - Individual MFEs (when fully initialized)
 */
const onMfeLoaded = (event: Event) => {
  const customEvent = event as CustomEvent<boolean>;
  loading.value = customEvent.detail;
};

// Register loader event listener
window.addEventListener("MFE_LOADED", onMfeLoaded);

/**
 * Handle navigation rail toggle requests from MFEs
 *
 * Event: mfe:folder-open
 * Payload:
 *   - true  → open navigation rail
 *   - false → close navigation rail
 *
 * Used when MFEs want to temporarily control layout
 * (e.g. opening a folder or deep view)
 */
window.addEventListener("mfe:folder-open", (event) => {
  const customEvent = event as CustomEvent<boolean>;
  if (customEvent.detail !== undefined) {
    userStore.setNavigationRail(customEvent.detail);
  }
});

/* ============================================================================
 * UI ACTIONS
 * ============================================================================
 */

/**
 * Toggle left navigation drawer manually
 * (Triggered by header / hamburger icon)
 */
const toggleLeftMenu = (isDrawerClosed: boolean): void => {
  userStore.setNavigationRail(isDrawerClosed);
};

/**
 * Handle alert modal confirmation
 *
 * Scenario:
 * - User hits usage limit for a feature
 * - Redirect them to Account & Billing page
 *
 * Routing Logic:
 * - Marketing users → SPA navigation
 * - Non-marketing users → full page redirect
 */
const handleAlertConfirm = () => {
  const accountId = userStore.getAccountId;
  const path = `/accounts/${accountId}/account-&-billing/subscriptions`;

  if (
    !userStore.getProductAccess("marketing") ||
    !userStore.getProductAccessOnRoles("marketing")
  ) {
    router.push(path);
  } else {
    navigateToUrl(path);
  }

  alertModalService.hide();
};

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_overlay = _resolveComponent("v-overlay")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_router_view = _resolveComponent("router-view")!
  const _component_v_main = _resolveComponent("v-main")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(TheHeader)),
    (_unref(trialDays) && _unref(trialDays).maxDays && showBanner.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_component_v_icon, { size: "18" }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createTextVNode(" mdi-information-outline ", -1)
            ]))]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_2, _toDisplayString(isTrialExpired.value ? 'Trial Ended' : 'Trial: ' + _unref(trialDays).maxDays + ' left until your trial ends.'), 1),
          _createElementVNode("a", {
            href: "#",
            onClick: navigateToBilling,
            class: "trial-link"
          }, "BUY NOW"),
          _createVNode(_component_v_icon, {
            size: "16",
            class: "trial-close-icon",
            onClick: hideBanner
          }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createTextVNode(" mdi-close ", -1)
            ]))]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_v_overlay, {
      "model-value": loading.value,
      class: "spa-loader",
      persistent: "",
      width: "100%",
      height: "100%",
      opacity: "0.25"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_row, {
          justify: "center",
          align: "center",
          class: "loader-row"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_progress_circular, {
              indeterminate: "",
              size: "48",
              width: "4",
              color: "primary"
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["model-value"]),
    _createVNode(_component_v_main, { class: "mt-16" }, {
      default: _withCtx(() => [
        _createVNode(_component_v_row, { "no-gutters": "" }, {
          default: _withCtx(() => [
            (showNavigation.value)
              ? (_openBlock(), _createBlock(_component_v_col, {
                  key: 0,
                  cols: "auto"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(TheUnifiedNavigation), {
                      class: "mt-16 content-area index",
                      "account-id": _unref(userStore).getAccountId,
                      "nav-blocks": _unref(navigation),
                      enableStoreMenu: saleNavigation.value,
                      collapsed: _unref(userStore).isNavigationRail,
                      "onUpdate:collapsed": toggleLeftMenu
                    }, {
                      "store-control": _withCtx(() => [...(_cache[3] || (_cache[3] = [
                        _createElementVNode("div", { id: "sale-channel-navigation" }, null, -1)
                      ]))]),
                      "top-block": _withCtx(() => [
                        _createVNode(OnboardingMenu)
                      ]),
                      _: 1
                    }, 8, ["account-id", "nav-blocks", "enableStoreMenu", "collapsed"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(_component_v_col, { cols: "12" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(_unref(DrawerHost), { ref: "drawerRef" }, null, 512),
                  _createVNode(_component_router_view)
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_unref(AlertModal), {
          show: _unref(alertModalState).show,
          state: alertModalItems.value,
          onConfirm: handleAlertConfirm,
          onCancel: _cache[0] || (_cache[0] = ($event: any) => (_unref(alertModalService).hide()))
        }, null, 8, ["show", "state"])
      ]),
      _: 1
    })
  ]))
}
}

})